import React from 'react';
import { useTheme } from '@mui/material';
import { SketchPicker, ColorResult } from 'react-color';
import DSBox from './DSBox';
import DSIconButton from './DSIconButton';

interface EyeDropperResult {
  sRGBHex: string;
}

interface EyeDropper {
  open(): Promise<EyeDropperResult>;
}

interface EyeDropperConstructor {
  new (): EyeDropper;
}

declare global {
  interface Window {
    EyeDropper?: EyeDropperConstructor;
  }
}

interface DSColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  open: boolean;
  onClose: () => void;
  disabled?: boolean;
}

const DSColorPicker: React.FC<DSColorPickerProps> = ({
  color,
  onChange,
  open,
  onClose,
  disabled = false,
}) => {
  const { spacing, palette } = useTheme();

  const supportsEyeDropper = 'EyeDropper' in window;

  const handleColorChange = (colorResult: ColorResult) => {
    onChange(colorResult.hex);
  };

  const handleEyeDropper = async () => {
    if (supportsEyeDropper && window.EyeDropper) {
      try {
        const eyeDropper = new window.EyeDropper();
        const result = await eyeDropper.open();
        onChange(result.sRGBHex);
      } catch (error) {
        console.log('EyeDropper cancelled or error:', error);
      }
    }
  };

  if (disabled || !open) {
    return null;
  }

  return (
    <>
      <DSBox
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
        }}
        onClick={onClose}
      />
      <DSBox
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10000,
          background: palette.paper.elevation12,
          borderRadius: spacing(2.5),
          boxShadow:
            '0px 9px 46px 8px rgba(0, 0, 0, 0.24), 0px 24px 38px 3px rgba(0, 0, 0, 0.28)',
          '& .sketch-picker': {
            background: 'transparent !important',
            boxShadow: 'none !important',
          },
          '& input': {
            background: palette.paper.elevation2 + ' !important',
            color: palette.text.primary + ' !important',
            borderRadius: spacing(1.25) + ' !important',
            textAlign: 'center !important',
          },
          '& label': {
            color: palette.white[56] + ' !important',
          },
        }}
      >
        <DSBox sx={{ position: 'relative' }}>
          {supportsEyeDropper && (
            <DSIconButton
              onClick={handleEyeDropper}
              sx={{
                position: 'absolute',
                top: spacing(-1.25),
                right: spacing(-1.25),
                width: spacing(4),
                height: spacing(4),
                background: palette.paper.elevation2,
                border: `${spacing(0.25)} solid ${palette.paper.elevation8}`,
                zIndex: 10,
                '&:hover': {
                  background: palette.paper.elevation5,
                  borderColor: palette.primary.main,
                },
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="m2 22 1-1h3l9-9" />
                <path d="M3 21v-3l9-9" />
                <path d="m15 6 3.4-3.4a2.1 2.1 0 1 1 3 3L18 9l.4.4a2.1 2.1 0 1 1-3 3l-3.8-3.8a2.1 2.1 0 1 1 3-3l.4.4Z" />
              </svg>
            </DSIconButton>
          )}
          <SketchPicker
            color={color || '#000010'}
            onChange={handleColorChange}
            disableAlpha
          />
        </DSBox>
      </DSBox>
    </>
  );
};

export default DSColorPicker;
